import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  Input as RouterInput,
  LOCALE_ID,
  OnInit,
  Signal,
  computed,
  effect,
} from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { validPhoneNumber } from '@platform/shared/utilities/phonenumber-validator';
import { OrganizationUnitDisallowedCharacters, OrganizationUnitValidationPattern } from '@platform/shared/utilities/validation-utility';
import { selectUser } from '@platform/platform.selectors';
import { Observable, distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { RootState } from '@root/app.state';
import { TranslateService } from '@ngx-translate/core';
import { Error, ResourceState, ResourceStatuses, filterSucceededResource } from '@platform/shared/resource.state';
import { CurrentUser } from '@platform/platform.state';
import { parsePhoneNumberWithError as parsePhoneNumber } from 'libphonenumber-js';
import { toDateOnly } from '@platform/shared/date-only';
import { PutUserProfileDto } from '@core/users/user.model';
import * as CoreActions from '@core/core.actions';
import * as RouterActions from '@platform/router.actions';
import moment from 'moment';
import { selectUserOrganizationUnits, selectUserProfile } from '@core/core.selectors';
import { User, UserProfile } from '@core/core.state';
import { formatDate } from '@angular/common';
import {
  OptionItem,
  emailValidator,
  maxLength,
  pattern,
  required,
  setControlDisabled,
  setControlEnabled,
} from '@platform/components/forms/forms.utils';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditUserComponent implements OnInit {
  @RouterInput() userId: string;

  form: UntypedFormGroup = this.fb.group({
    firstName: ['', [required, maxLength(200)]],
    lastName: ['', [required, maxLength(200)]],
    organizationUnit: ['', [required, maxLength(200), pattern(OrganizationUnitValidationPattern)]],
    mobileNumber: ['', [validPhoneNumber(), required]],
    mail: ['', [required, maxLength(200), emailValidator()]],
    language: ['German', [required]],
    active: ['Active', [required]],
    externalId: [''],
    areMailNotificationsOn: [true],
    notificationInterval: ['Weeks1', [required]],
    activeUntil: this.fb.control<Date | null>(null),
  });

  filteredOrganizationUnits$: Observable<string[]>;
  showExternalId = true;
  lastSuccessfulLogin: string;
  createdAt: string;
  disableProfilePictureChange = false;

  isInternalUser: Signal<boolean>;
  isActiveUser: Signal<boolean | undefined>;

  intervalOptionsTranslated: OptionItem[] = [];

  savePending: Signal<boolean>;
  currentUserId: Signal<string>;
  currentUserIsInternal: Signal<boolean>;
  userDeputies: User[] = [];
  changePasswordUrl: string;
  thumbnailPhoto: string;

  private userProfileResource: Signal<ResourceState<UserProfile> | undefined>;
  private currentUser: Signal<CurrentUser | undefined>;
  private userProfile: Signal<UserProfile | undefined>;
  private organizationUnits: string[] = [];
  private startDate = moment();

  constructor(
    private fb: FormBuilder,
    private store$: Store<RootState>,
    private translate: TranslateService,
    @Inject(LOCALE_ID) private locale: string,
    private destroyRef: DestroyRef,
  ) {
    // current logged in user
    this.currentUser = toSignal(store$.pipe(select(selectUser), filterSucceededResource()));
    this.currentUserId = computed(() => this.currentUser()?.id!);
    this.currentUserIsInternal = computed(() => this.currentUser()?.isInternal || false);

    // edited user
    this.userProfileResource = toSignal(store$.pipe(select(selectUserProfile)));
    this.savePending = computed(() => this.userProfileResource()?.status === ResourceStatuses.PENDING);

    // user profile is loaded in Guard by "userId" route param
    this.userProfile = computed(() => this.userProfileResource()?.data);
    this.isInternalUser = computed(() => Boolean(this.userProfile()?.isInternal));

    this.filteredOrganizationUnits$ = this.form.get('organizationUnit')!.valueChanges.pipe(
      startWith(''),
      map((val) => this.filterOrganizationUnits(val)),
    );

    // on toggle user status
    this.isActiveUser = toSignal(
      this.form.get('active')!.valueChanges.pipe(
        map((v) => v === 'Active'),
        distinctUntilChanged(), // valueChanges emits even when control value was not changed but 'disabled' was changed.
      ),
    );

    // profile user loaded
    effect(
      () => {
        if (!this.currentUser()) {
          return;
        }

        if (!this.userProfile()) {
          return;
        }

        if (this.isInternalUser()) {
          [
            'firstName',
            'lastName',
            'organizationUnit',
            'mobileNumber',
            'mail',
            'language',
            'active',
            'areMailNotificationsOn',
            'notificationInterval',
          ].forEach((controlName) => setControlDisabled(this.form.get(controlName)));
        } else {
          const controlsToChange = [
            'firstName',
            'lastName',
            'organizationUnit',
            'mobileNumber',
            'mail',
            'language',
            'activeUntil',
            'areMailNotificationsOn',
            'notificationInterval',
          ];
          if (this.isActiveUser()) {
            controlsToChange.forEach((controlName) => setControlEnabled(this.form.get(controlName)));
            if (this.form.get('areMailNotificationsOn')!.value === false) {
              this.form.get('notificationInterval')?.disable();
            }
          } else {
            controlsToChange.forEach((controlName) => setControlDisabled(this.form.get(controlName)));
          }
        }
      },
      { allowSignalWrites: true }, // without this option effect throws error when 'active' control status/disabled is changed (line 138)
    );
  }

  get phoneNumberValidationRules() {
    return {
      invalidPhoneNumber: 'users.user-form.error.mobile-number-invalid',
    };
  }

  get organizationUnitValidationRules() {
    return {
      pattern: () => this.getOrgUnitDisallowedCharactersValidationMessage(),
    };
  }

  get mailValidationRules() {
    return {
      backendError: () => this.form.get('mail')!.getError('backendError'),
    };
  }

  ngOnInit(): void {
    if (this.userId === this.currentUserId()) {
      this.store$.dispatch(RouterActions.go({ path: ['user-profile'] }));
      return;
    }

    this.store$.pipe(select(selectUserProfile), takeUntilDestroyed(this.destroyRef)).subscribe((state) => this.onUserProfileLoaded(state));
    this.form
      .get('areMailNotificationsOn')!
      .valueChanges.pipe(startWith(false), distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        console.log('areMailNotificationsOn', value);
        setControlEnabled(this.form.get('notificationInterval'), Boolean(value));
      });

    this.store$
      .pipe(
        select(selectUserOrganizationUnits),
        filter((state) => state.status === ResourceStatuses.SUCCEEDED && !!state.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => (this.organizationUnits = state.data));
  }

  save() {
    let phoneNumber;
    if (this.form.get('mobileNumber')!.value) {
      phoneNumber = parsePhoneNumber(this.form.get('mobileNumber')!.value, 'CH');
    }
    let activeUntilDate = this.form.get('activeUntil')!.value;
    if (activeUntilDate != null) {
      activeUntilDate = toDateOnly(activeUntilDate);
    }

    const user: PutUserProfileDto = {
      firstName: this.form.get('firstName')!.value,
      lastName: this.form.get('lastName')!.value,
      organizationUnit: this.form.get('organizationUnit')!.value,
      mobileNumber: phoneNumber?.number ?? '',
      email: this.form.get('mail')!.value,
      language: this.form.get('language')!.value,
      isActive: this.form.get('active')!.value === 'Active',
      areMailNotificationsOn: this.form.get('areMailNotificationsOn')!.value,
      notificationInterval: this.form.get('notificationInterval')!.value,
      activeUntil: activeUntilDate,
    };

    this.store$.dispatch(CoreActions.updateUserProfile({ userId: this.userId, userProfile: user }));
  }

  public getOrgUnitDisallowedCharactersValidationMessage(): string {
    return this.translate.instant('users.user-form.error.invalid-format') + OrganizationUnitDisallowedCharacters;
  }

  public navigateBack($event: any) {
    $event.preventDefault();
    $event.stopPropagation();
    this.store$.dispatch(RouterActions.back());
  }

  private filterOrganizationUnits(val: string): string[] {
    if (!val) {
      return this.organizationUnits;
    }

    return this.organizationUnits.filter((organizationUnit) => organizationUnit.toLowerCase().includes(val.toLowerCase()));
  }

  private onUserProfileLoaded(state: ResourceState<UserProfile>) {
    if (state.data) {
      if (state.status === ResourceStatuses.SUCCEEDED) {
        const profile = this.userProfile()!;
        this.userDeputies = state.data.deputies;
        this.changePasswordUrl = state.data.changePasswordUrl;
        this.thumbnailPhoto = state.data.thumbnailPhoto;

        this.form.get('externalId')!.disable();

        this.lastSuccessfulLogin =
          profile.lastSuccessfulLoginAt !== null
            ? formatDate(profile.lastSuccessfulLoginAt, 'dd.MM.yyyy HH:mm:ss', this.locale)
            : this.translate.instant('users.user-form.no-activity');

        // for external user
        this.createdAt =
          profile.createdAt !== null
            ? formatDate(profile.createdAt, 'dd.MM.yyyy HH:mm:ss', this.locale)
            : this.translate.instant('users.user-form.no-activity');

        const {
          firstName,
          lastName,
          organizationUnit,
          mobileNumber,
          mail,
          language,
          isActive,
          externalId,
          areMailNotificationsOn,
          notificationInterval,
          activeUntil,
        } = profile;

        this.form.setValue({
          firstName,
          lastName,
          organizationUnit,
          mobileNumber,
          mail,
          language,
          active: isActive ? 'Active' : 'Inactive',
          externalId,
          areMailNotificationsOn,
          notificationInterval,
          activeUntil,
        });
      }

      this.intervalOptionsTranslated = ['Weeks1', 'Hours24', 'Hours12', 'Hours4', 'Hours1', 'Minutes15'].map((value) => ({
        value,
        label: this.translate.instant(`users.user-profile.notifications.interval.${value.toLowerCase()}`),
      }));
    }

    if (state.status === ResourceStatuses.FAILED) {
      if (state.error?.errors) {
        this.setFormErrors(state.error?.errors);
      }
    }
  }

  private setFormErrors(errors: Error) {
    if (errors['Mail']) {
      this.form.get('mail')!.setErrors({
        backendError: errors['Mail'][0],
      });
    }
  }
}
